import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Mail, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Shopper emails themselves a summary of the configured shade (with room render / swatch when available).
export const EmailDesignDialog = ({ open, onOpenChange, buildPayload, hasRoomRender }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return toast.error('Please enter a valid email address');
    setSending(true);
    try {
      await axios.post(`${API_URL}/design-email`, { ...(await buildPayload()), email, name: name.trim() || undefined });
      toast.success(`Design summary sent to ${email}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="email-design-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2"><Mail className="w-5 h-5 text-amber-600" /> Email my design</DialogTitle>
          <DialogDescription>
            We'll send a summary of your fabric, size and options{hasRoomRender ? ', including the render of your own room' : ''}. Nothing else — no marketing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1" autoFocus data-testid="email-design-input" onKeyDown={(e) => e.key === 'Enter' && send()} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-neutral-500">Name (optional)</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mt-1" data-testid="email-design-name" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={sending || !email} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="email-design-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send summary
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailDesignDialog;
